import { api } from './api'
import type { OverrideRequest, ActiveOverride } from './accessControlService'

export interface RequestEmergencyOverrideParams {
  patientId: string
  fields: string[]
  reason: string
}

export interface ApproveOverrideParams {
  overrideId: string
  expiresInHours?: number
}

export interface RejectOverrideParams {
  overrideId: string
  rejectionReason?: string
}

/**
 * Emergency Override Service
 * 
 * Handles emergency access override requests, approvals, and management.
 * All operations require appropriate permissions (patients:override:emergency).
 */
export const emergencyOverrideService = {
  /**
   * Request emergency access to restricted fields
   */
  request: async (params: RequestEmergencyOverrideParams): Promise<OverrideRequest> => {
    const response = await api.post<OverrideRequest>('/emergency-override/request', {
      patientId: params.patientId,
      fields: params.fields,
      reason: params.reason,
    })
    return response.data
  },

  /**
   * Approve a pending override request
   * Requires patients:override:emergency permission
   */
  approve: async (params: ApproveOverrideParams): Promise<OverrideRequest> => {
    const response = await api.post<OverrideRequest>('/emergency-override/approve', {
      overrideId: params.overrideId,
      expiresInHours: params.expiresInHours || 24, // Default 24 hours
    })
    return response.data
  },

  /**
   * Reject a pending override request
   * Requires patients:override:emergency permission
   */
  reject: async (params: RejectOverrideParams): Promise<OverrideRequest> => {
    const response = await api.post<OverrideRequest>('/emergency-override/reject', {
      overrideId: params.overrideId,
      rejectionReason: params.rejectionReason,
    })
    return response.data
  },

  /**
   * Get active overrides for a user/patient combination
   */
  getActive: async (patientId: string, fields?: string[]): Promise<ActiveOverride[]> => {
    const response = await api.get<ActiveOverride[]>('/emergency-override/active', {
      params: {
        patientId,
        fields: fields?.join(','),
      },
    })
    return response.data
  },

  /**
   * Get pending override requests (for admins)
   */
  getPending: async (): Promise<OverrideRequest[]> => {
    const response = await api.get<OverrideRequest[]>('/emergency-override/pending')
    return response.data
  },

  /**
   * Get a specific override by ID
   */
  getById: async (overrideId: string): Promise<OverrideRequest> => {
    const response = await api.get<OverrideRequest>(`/emergency-override/${overrideId}`)
    return response.data
  },
}
